import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadixSelect } from "@/components/ui/select";
import useGetCategoriesService from "@/service-hooks/use-get-cagetories-service";

interface SaveComponentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, description: string, category: string) => void;
}

const SaveComponentDialog: React.FC<SaveComponentDialogProps> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [error, setError] = useState<string | null>(null);

  // The breadcrumb keeps this dialog mounted on every page, so only fetch the categories once it is opened.
  const { data: categories } = useGetCategoriesService(isOpen);

  const handleSubmit = () => {
    if (!name.trim()) {
      setError("Component name is required");
      return;
    }
    if (!category) {
      setError("Category is required");
      return;
    }

    onSave(name.trim(), description.trim(), category);
    handleClose();
  };

  const handleClose = () => {
    setName("");
    setDescription("");
    setCategory("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="bg-surface-elevated text-text-primary border-surface-border"
        aria-describedby="dialog-description"
      >
        <DialogHeader>
          <DialogTitle className="text-text-primary">
            Save as Component
          </DialogTitle>
        </DialogHeader>

        {/* Hidden description for screen readers */}
        <span id="dialog-description" className="sr-only">
          Create a reusable component from the current composition
        </span>

        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Component Name *
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter component name"
              className="w-full bg-surface-active text-white placeholder:text-muted"
            />
            {error && <div className="text-error text-sm mt-1">{error}</div>}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Category *
            </label>
            <RadixSelect
              options={
                categories?.data?.map((category) => ({
                  label: category.name,
                  value: category._id,
                })) ?? []
              }
              value={
                categories?.data?.find((cat) => cat._id === category)?.name ??
                ""
              }
              onChange={(value) => setCategory(value)}
              placeholder="Select category"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter component description"
              className="w-full bg-surface-input border-surface-border text-text-primary placeholder:text-muted"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              onClick={handleClose}
              variant="outline"
              className="bg-surface border border-surface-border hover:bg-surface-hover hover:border-surface-border text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-primary hover:bg-primary-dark text-white"
              disabled={!name.trim() || !category}
            >
              Save Component
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SaveComponentDialog;
